import React, { Component, createRef } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Button,
  InputGroup,
  FormControl,
  Alert,
  Card,
  Table,
  Form,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import RangeSlider from "react-bootstrap-range-slider";

import {
  FabricImage,
  Textbox,
  Line,
} from "fabric";

import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import { CopyImageButton } from "./CopyImageButton";
import { resolvePosition } from "./templatePositions";
import OpenAI from "openai";
import { getDalleOutputs, setDalleOutputs, clearDalleOutputs } from "./dalleOutputsStorage";

const convertImageToJpeg = (imageUrl, callback) => {
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.src = imageUrl;

  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const jpegImageURL = canvas.toDataURL("image/jpeg", 0.9);
    callback(jpegImageURL);
  };

  img.onerror = (error) => {
    console.error("Error loading the image:", error);
  };
};

const addImageToCanvas = (canvasRef, imageUrl, readyForPoster, setReadyForPoster, switchTab, templateStrings, templateElements) => {

  let settings = { crossOrigin: "anonymous", left: 512, top: 512, originX: 'center', originY: 'center', scaleX: 1, scaleY: 1, selectable: false, evented: false, hoverCursor: "pointer" }

  if (readyForPoster) {
    settings = { crossOrigin: "anonymous", left: 512, top: 512, originX: 'center', originY: 'center', scaleX: 1, scaleY: 1 }
  }

  FabricImage.fromURL(imageUrl)
    .then((img) => {
      img.set(settings);

      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.add(img);

      // Remove any stale template textboxes before adding fresh ones
      const existingObjects = canvas.getObjects();
      const staleTemplates = existingObjects.filter(obj =>
        obj.type === 'textbox' && (obj._templateId || templateStrings.includes(obj.text))
      );
      staleTemplates.forEach(obj => canvas.remove(obj));

      const imgW = img.width * img.scaleX;
      const imgH = img.height * img.scaleY;
      let imgLeftCorner = img.left;
      let imgTopCorner = img.top;
      if (img.originX === 'center') {
        imgLeftCorner -= imgW / 2;
      } else if (img.originX === 'right') {
        imgLeftCorner -= imgW;
      }
      if (img.originY === 'center') {
        imgTopCorner -= imgH / 2;
      } else if (img.originY === 'bottom') {
        imgTopCorner -= imgH;
      }

      for (const el of templateElements) {
        const pos = resolvePosition(el.position, imgLeftCorner, imgTopCorner, imgW, imgH);
        const textbox = new Textbox(el.text, {
          left: pos.left,
          top: pos.top,
          width: pos.width,
          originX: 'left',
          originY: 'top',
          fontSize: el.fontSize,
          fill: el.fill,
          fontFamily: el.fontFamily,
          fontWeight: el.fontWeight || 'normal',
          textAlign: el.textAlign || 'center',
          stroke: el.stroke,
          strokeWidth: el.strokeWidth,
          ...(el.id ? { _templateId: el.id } : {}),
        });
        canvas.add(textbox);
      }

      canvas.renderAll();
    })
    .catch((error) => {
      console.error("Error loading image:", error);
    });

  setReadyForPoster(true);
  switchTab('poster');
}

const formatTime = (timeStart) => {
  if (timeStart == null) {
    return "---";
  }
  const currentTime = new Date();
  const difference = currentTime - timeStart;
  return (difference / 1000).toFixed(1);
};

class Dalle extends Component {
  constructor(props) {
    super(props);
    this.state = {
      outputs: [],
      isPendingImage: false,
      hasStarted: false,
      isPendingText: false,
      isCurrentlyRunning: false,
      numIterations: 8,
      sharedUrl: null,
      shareProcessing: null,
      shareUrl: null,
      keepGoingIterations: 0,
      stopNow: false,
      showCustomPromptBox: false,
      promptSelectValue: "default",
      isLoadingShare: false,
      generationStartTime: new Date(),
      quality: "medium",
      showScroll: false,
      showScrollBottom: false,
      clearConfirm: false,
      highlightStart: -1,
    };
    this.promptRef = createRef();
    this.overlayRef = createRef();
    this.urlInputRef = createRef();
    this.timeRef = createRef();
    this.timeRef2 = createRef();
    this.instructionsRef = createRef();
    this.pendingRef = createRef();
    this.pendingRefMobile = createRef();
    this.lastImageRef = createRef();

    this.generateImage = this.generateImage.bind(this);
    this.renderRows = this.renderRows.bind(this);
    this.saveImagesToLocalStorage = this.saveImagesToLocalStorage.bind(this);
    this.reset = this.reset.bind(this);
    this.handleClearClick = this.handleClearClick.bind(this);
    this.generateModificationButtons =
      this.generateModificationButtons.bind(this);
    this.modPrompt = this.modPrompt.bind(this);
    this.checkScrollTop = this.checkScrollTop.bind(this);
    this.scrollTop = this.scrollTop.bind(this);
    this.scrollBottom = this.scrollBottom.bind(this);
  }

  componentDidMount() {
    window.addEventListener("scroll", this.checkScrollTop);

    if (this.props.storageKey) {
      getDalleOutputs(this.props.storageKey)
        .then((outputs) => {
          if (outputs && Array.isArray(outputs) && outputs.length > 0) {
            this.setState({ outputs });
          } else {
            this.migrateFromLocalStorage();
          }
        })
        .catch(() => this.migrateFromLocalStorage());
    }
  }

  migrateFromLocalStorage() {
    if (!this.props.storageKey) return;
    try {
      const stored = localStorage.getItem(this.props.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.setState({ outputs: parsed });
          setDalleOutputs(this.props.storageKey, parsed).catch(() => {});
        }
      }
    } catch (e) {
      console.error("Failed to load image outputs from localStorage:", e);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.checkScrollTop);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.storageKey && prevState.outputs !== this.state.outputs) {
      this.saveImagesToLocalStorage(this.state.outputs);
    }
    if (
      prevProps.activeTab !== this.props.activeTab &&
      this.props.activeTab === "dalle"
    ) {
      let outputNum = this.state.outputs?.length;

      if (!outputNum && this.props.storageKey) {
        getDalleOutputs(this.props.storageKey).then((parsed) => {
          if (parsed && Array.isArray(parsed) && parsed.length > 0) {
            this.setState({ outputs: parsed });
          }
        }).catch(() => {});
      }

      if (this.props.dallePromptText && this.promptRef.current) {
        this.promptRef.current.value = this.props.dallePromptText;
        const textarea = this.promptRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = Math.max(64, textarea.scrollHeight) + 'px';
        if (this.props.setDallePromptText) {
          this.props.setDallePromptText('');
        }
        this.setState({ highlightStart: 0 }, () => {
          this.syncOverlay();
        });
        this.promptRef.current.focus({ preventScroll: true });
      } else if (outputNum < 1) {
        this.promptRef.current.focus({ preventScroll: true });
      }
    }
  }

  checkScrollTop() {
    const scrollPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const isScrollable = documentHeight > windowHeight + 100;
    const showBoth = isScrollable && (scrollPosition > 200 || scrollPosition + windowHeight < documentHeight - 200);

    if (this.state.showScroll !== showBoth) {
      this.setState({ showScroll: showBoth });
    }
    if (this.state.showScrollBottom !== showBoth) {
      this.setState({ showScrollBottom: showBoth });
    }
  }

  scrollTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  scrollBottom() {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  }

  fetchImage = async (prompt) => {
    this.setState({
      isPendingImage: true,
      isPendingText: false,
      hasStarted: true,
      isCurrentlyRunning: true,
    }, () => {
      setTimeout(() => {
        const desktopEl = this.pendingRef.current;
        const mobileEl = this.pendingRefMobile.current;
        const el = (desktopEl && desktopEl.offsetParent !== null) ? desktopEl : mobileEl;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    });

    const openai = new OpenAI({
      apiKey: this.props.openaiApiKey,
      dangerouslyAllowBrowser: true,
    });

    let request_payload = {
      model: this.props.imageModel,
      prompt: prompt,
      n: 1,
      size: "1024x1024",
    }
    if (this.props.imageModel === "dall-e-3") {
      request_payload.response_format = "b64_json";
    } else if (this.props.imageModel?.startsWith("gpt-image")) {
      request_payload.quality = this.state.quality;
    }

    try {
      let image = await openai.images.generate(request_payload);

      if (this.state.stopNow) {
        return;
      }

      const base64Image = image.data[0].b64_json;
      convertImageToJpeg(
        `data:image/png;base64,${base64Image}`,
        (image_data) => {
          image.data[0].image_data = image_data;
          image.data[0].b64_json = null;

          this.setState(
            (prevState) => ({
              outputs: [...prevState.outputs, { image: image }],
              shareUrl: null,
              isPendingImage: false,
              isPendingText: true,
            }),
            () => {
              this.saveImagesToLocalStorage(this.state.outputs);
              this.setState({
                isPendingImage: false,
                isPendingText: false,
                isCurrentlyRunning: false,
              });
              clearInterval(this.timerID);
              setTimeout(() => {
                if (this.lastImageRef.current) {
                  this.lastImageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 200);
            }
          );
        }
      );
    } catch (err) {
      console.log(err);
      if ("message" in err) {
        this.props.setErrorMessage(err.message);
      } else {
        this.props.setErrorMessage(err.toString());
      }
      let outputs = this.state.outputs;
      if (this.state.outputs.length % 2 != 0) {
        outputs = [...outputs];
        outputs.pop();
      }
      this.setState({
        isPendingImage: false,
        isPendingText: false,
        isCurrentlyRunning: false,
        outputs: outputs,
      });
      clearInterval(this.timerID);
    }
  };

  saveImagesToLocalStorage(newOutputs) {
    if (!this.props.storageKey) return;
    setDalleOutputs(this.props.storageKey, newOutputs).catch((e) => {
      console.error("Failed to save image outputs:", e);
    });
  }

  generateImage(e) {
    e.preventDefault();

    if (this.props.setImageGenerationStarted) {
      this.props.setImageGenerationStarted(true);
    }

    const promptValue = this.promptRef.current.value;

    this.promptRef.current.select();

    this.setState(
      (prevState) => ({
        outputs: [...prevState.outputs, { raw_text: promptValue, sourcePromptIndex: this.props.dalleSourcePromptIndex }],
        generationStartTime: new Date(),
      }),
      () => {
        this.saveImagesToLocalStorage(this.state.outputs);
        this.fetchImage(promptValue);
        this.timerID = setInterval(
          () => this.updateTimeDisplay(),
          100
        );
      }
    );
  }

  updateTimeDisplay() {
    const out = formatTime(this.state.generationStartTime);
    if (this.timeRef.current) {
      this.timeRef.current.textContent = out;
    }
    if (this.timeRef2.current) {
      this.timeRef2.current.textContent = out;
    }
  }

  reset() {
    this.setState({
      outputs: [],
    });
    if (this.props.storageKey) {
      clearDalleOutputs(this.props.storageKey).catch(() => {});
      try {
        localStorage.setItem(this.props.storageKey, JSON.stringify([]));
      } catch (e) {}
    }
    this.promptRef.current.focus();
  }

  handleClearClick() {
    if (this.state.clearConfirm) {
      this.reset();
      this.setState({ clearConfirm: false });
      return;
    }
    this.setState({ clearConfirm: true });
    setTimeout(() => {
      this.setState({ clearConfirm: false });
    }, 5000);
  }

  generateModificationButtons(prompt) {
    if (this.props.workshopConfig.hideModButtons && !this.props.workshopConfig.hideModButtonsForSource) {
      return <></>;
    }
    let out = [];
    const { modButtons, styleButtons } = this.props.workshopConfig;

    for (let i = 0; i < modButtons.length; i++) {
      out.push(
        <Button
          variant="outline-secondary"
          key={i}
          onClick={(e) => this.modPrompt(e, prompt, modButtons[i]["val"], "mod")}
          style={{ margin: "3px", padding: "5px", fontSize: "12px", height: "40px", display: "inline-block", verticalAlign: "top" }}
        >
          {modButtons[i]["title"]}
        </Button>
      );
    }

    let out2 = [];

    for (let i = 0; i < styleButtons.length; i++) {
      out2.push(
        <Button
          variant="outline-secondary"
          key={i + "style"}
          onClick={(e) => this.modPrompt(e, prompt, styleButtons[i]["val"], "style")}
          style={{ margin: "3px", padding: "5px", fontSize: "12px" }}
        >
          <img
            src={"dalle-styles/" + styleButtons[i]["img"]}
            style={{ width: "60px", height: "60px", objectFit: "cover" }}
          />
          <br />
          {styleButtons[i]["title"]}
        </Button>
      );
    }

    return (
      <>
        <div style={{ marginTop: "25px" }}>
          <strong>Prompt engineering</strong>
        </div>
        {out}
        <br />
        <strong>Styles</strong>
        <br />
        {out2}
      </>
    );
  }

  stripSuffixesOfType(promptText, type) {
    const { modButtons = [], styleButtons = [] } = this.props.workshopConfig || {};
    const buttons = type === "style" ? styleButtons : modButtons;
    const vals = (buttons || []).map((b) => b.val).filter(Boolean);
    let base = (promptText || "").trim();
    let changed = true;
    while (changed) {
      changed = false;
      for (const val of vals) {
        const withDot = ". " + val;
        const withSpace = " " + val;
        if (base.endsWith(withDot)) {
          base = base.slice(0, base.length - withDot.length).trim();
          changed = true;
          break;
        }
        if (base.endsWith(withSpace)) {
          base = base.slice(0, base.length - withSpace.length).trim();
          changed = true;
          break;
        }
        if (base.endsWith(val)) {
          base = base.slice(0, base.length - val.length).trim();
          changed = true;
          break;
        }
      }
    }
    return base;
  }

  modPrompt(evt, prompt, addText, type) {
    if (!this.promptRef.current) {
      return;
    }
    const base = (type === "style" || type === "mod")
      ? this.stripSuffixesOfType(prompt, type)
      : this.stripSuffixesOfType(this.stripSuffixesOfType(prompt, "style"), "mod");
    const newValue = base ? base + ". " + addText : addText;
    this.promptRef.current.value = newValue;
    const textarea = this.promptRef.current;
    textarea.style.height = "auto";
    textarea.style.height = Math.max(64, textarea.scrollHeight) + "px";
    const highlightStart = base ? base.length + 2 : 0;
    this.setState({ highlightStart }, () => {
      this.syncOverlay();
    });
    this.promptRef.current.focus();
  }

  syncOverlay() {
    if (!this.promptRef.current || !this.overlayRef.current) return;
    const text = this.promptRef.current.value;
    const { highlightStart } = this.state;
    if (highlightStart >= 0 && highlightStart < text.length) {
      const before = text.substring(0, highlightStart);
      const after = text.substring(highlightStart);
      this.overlayRef.current.innerHTML =
        this.escapeHtml(before) + '<mark style="background-color:#f4e4bc;color:transparent;border-radius:3px">' + this.escapeHtml(after) + '</mark>';
    } else {
      this.overlayRef.current.innerHTML = this.escapeHtml(text);
    }
    this.overlayRef.current.scrollTop = this.promptRef.current.scrollTop;
    if (this.overlayRef.current) {
      this.overlayRef.current.style.height = this.promptRef.current.style.height;
    }
  }

  escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
  }

  duplicateImage() {
    this.setState((prevState) => ({
      outputs: [...prevState.outputs, { raw_text: prevState.outputs[0].raw_text }, { image: prevState.outputs[1].image }]
    }), () => {
      this.saveImagesToLocalStorage(this.state.outputs);
    });
  }

  renderRows() {
    let rows = [];
    let this_col = [];
    let counter = 0;
    let buttons = <></>;
    const { templateStrings, templateElements, createPosterButtonText } = this.props.workshopConfig;

    let currentSourcePromptIndex = null;
    let currentRawText = '';

    for (let i = 0; i < this.state.outputs.length; i++) {
      let o = this.state.outputs[i];
      if ("image" in o) {
        const hideModButtonsFn = this.props.workshopConfig.hideModButtonsForSource;
        const hidePosterButton = typeof hideModButtonsFn === 'function'
          ? hideModButtonsFn(currentSourcePromptIndex)
          : this.props.workshopConfig.hideModButtons;
        this_col.push(
          <td key={"image-" + counter} className="d-none d-lg-table-cell" style={{ overflow: "hidden", verticalAlign: "middle" }}>
            <div style={{ width: "100%", maxWidth: "100%", textAlign: "center" }}>
              <div style={{ width: "100%", maxWidth: "100%", margin: "0 auto" }}>
                <img
                  src={o["image"].data[0].image_data}
                  style={{
                    maxWidth: "100%",
                    width: "auto",
                    height: "auto",
                    maxHeight: "70vh",
                    display: "block",
                    margin: "0 auto",
                    objectFit: "contain",
                  }}
                />
                {!hidePosterButton && (
                  <div className="dalle-create-poster-btn-wrap">
                    <Button
                      className="stageone-btn stageone-btn-glow poster-pulse dalle-create-poster-btn"
                      onClick={(e) => { addImageToCanvas(this.props.fabricCanvasRef, o["image"].data[0].image_data, this.props.readyForPoster, this.props.setReadyForPoster, this.props.switchTab, templateStrings, templateElements); if (this.props.setVideoPromptText) this.props.setVideoPromptText(currentRawText); if (this.props.setVideoReferenceImageUrl) this.props.setVideoReferenceImageUrl(o["image"].data[0].image_data); }}
                    >
                      {createPosterButtonText}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </td>
        );
        rows.push(
          <tr
            ref={this.lastImageRef}
            className="d-none d-lg-table-row"
            style={{ verticalAlign: "middle" }}
            key={counter}
          >
            {this_col}
          </tr>
        );
        // Mobile view
        rows.push(
          <tr className="d-lg-none" ref={this.lastImageRef} key={"mobile-image-" + counter}>
            <td colSpan={2} style={{ textAlign: "center", padding: "0", width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
              <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <img
                  src={o["image"].data[0].image_data}
                  style={{ maxHeight: "70vh", maxWidth: "100%", width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                />
                {!hidePosterButton && (
                  <div className="dalle-create-poster-btn-wrap">
                    <Button
                      className="stageone-btn stageone-btn-glow poster-pulse dalle-create-poster-btn"
                      onClick={(e) => {
                        addImageToCanvas(
                          this.props.fabricCanvasRef,
                          o["image"].data[0].image_data,
                          this.props.readyForPoster,
                          this.props.setReadyForPoster,
                          this.props.switchTab,
                          templateStrings,
                          templateElements
                        );
                        if (this.props.setVideoPromptText) this.props.setVideoPromptText(currentRawText);
                        if (this.props.setVideoReferenceImageUrl) this.props.setVideoReferenceImageUrl(o["image"].data[0].image_data);
                      }}
                    >
                      {createPosterButtonText}
                    </Button>
                  </div>
                )}
              </div>
            </td>
          </tr>
        );
        // Add buttons or instruction box as a new row.
        const afterImageContentProp = this.props.workshopConfig.afterImageContent;
        const imageData = o["image"].data[0].image_data;
        const onCreatePoster = () => { addImageToCanvas(this.props.fabricCanvasRef, imageData, this.props.readyForPoster, this.props.setReadyForPoster, this.props.switchTab, templateStrings, templateElements); if (this.props.setVideoPromptText) this.props.setVideoPromptText(currentRawText); if (this.props.setVideoReferenceImageUrl) this.props.setVideoReferenceImageUrl(imageData); };
        const afterImageContent = typeof afterImageContentProp === 'function'
          ? afterImageContentProp({
              imageData,
              onCreatePoster,
              sourcePromptIndex: currentSourcePromptIndex,
              workshopId: this.props.workshopId,
              studentId: this.props.nodeId,
            })
          : afterImageContentProp;
        if (afterImageContent) {
          rows.push(
            <tr key={"after-image-" + counter}>
              <td colSpan="2">
                {afterImageContent}
              </td>
            </tr>
          );
        }
        rows.push(
          <tr key={"buttons-" + counter}>
            <td colSpan="2">
              {hidePosterButton ? <></> : buttons}
            </td>
          </tr>
        );
        this_col = [];
      } else if ("raw_text" in o) {
        currentRawText = o.raw_text;
        if (o.sourcePromptIndex != null) {
          currentSourcePromptIndex = o.sourcePromptIndex;
        }
        let toptext = <></>;
        buttons = this.generateModificationButtons(
          this.state.outputs[i]["raw_text"]
        );
        if (counter > 0) {
          toptext = (
            <>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "large",
                  fontWeight: "bold",
                }}
              ></div>
            </>
          );
        } else {
          toptext = (
            <>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "large",
                  fontWeight: "bold",
                }}
              ></div>
            </>
          );
        }
        this_col.push(
          <td key={"raw-text-" + counter} className="d-none d-lg-table-cell">
            {toptext}
            <div style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "2px solid #007bff",
              borderRadius: "8px",
              padding: "10px 15px",
              margin: "5px 0",
              position: "relative"
            }}>
              <div style={{
                color: "#ddd",
                fontWeight: "bold",
                fontSize: "12px",
                fontStyle: "italic",
                marginBottom: "5px"
              }}>
                Prompt for {this.props.imageModel?.startsWith("gpt-image") ? "GPT Image" : "DALL·E 3"}:
              </div>
              {o["raw_text"]}
            </div>
          </td>
        );
        // Mobile view
        rows.push(
          <tr className="d-lg-none" key={"mobile-raw-text-" + counter}>
            <td>
              {toptext}
              <div style={{
                backgroundColor: "#007bff",
                color: "white",
                border: "2px solid #007bff",
                borderRadius: "8px",
                padding: "10px 15px",
                margin: "5px 0",
                position: "relative"
              }}>
                <div style={{
                  color: "#ddd",
                  fontWeight: "bold",
                  fontSize: "12px",
                  fontStyle: "italic",
                  marginBottom: "5px"
                }}>
                  Prompt for {this.props.imageModel?.startsWith("gpt-image") ? "GPT Image" : "DALL·E 3"}:
                </div>
                {o["raw_text"]}
              </div>
              {/* <div style={{ marginTop: "auto" }}>{buttons}</div> */}
            </td>
          </tr>
        );
      }
      counter++;
    }
    // push anything else
    if (this_col.length === 1) {
      let pending1 = <></>;
      let pending2 = <></>;
      if (this.state.isPendingImage) {
        pending1 = (
          <>
            Generating image...
            <br />
            <br />
            <Spinner animation="border" />
            <br />
            <span ref={this.timeRef}>0.0</span>
          </>
        );
        pending2 = (
          <>
            Generating image...
            <br />
            <br />
            <Spinner animation="border" />
            <br />
            <span ref={this.timeRef2}>0.0</span>
          </>
        );
      }
      rows.push(
        <tr
          ref={this.state.isPendingImage ? this.pendingRef : undefined}
          className="d-none d-lg-table-row"
          style={{ verticalAlign: "middle" }}
          key={counter + 1}
        >
          {this_col}
          <td style={{ textAlign: "center" }}>{pending1}</td>
        </tr>
      );
      // Mobile view for pending
      if (this.state.isPendingImage) {
        rows.push(
          <tr className="d-lg-none" ref={this.pendingRefMobile} key={"mobile-pending-" + (counter + 1)}>
            <td style={{ textAlign: "center" }}>{pending2}</td>
          </tr>
        );
      }
    } else if (this_col.length > 1) {
      rows.push(
        <tr className="d-none d-lg-table-row" key={counter + 1}>
          {this_col}
        </tr>
      );
    }

    if (this.state.isPendingText) {
      rows.push(
        <tr
          className="d-none d-lg-table-row"
          key={-1}
          style={{ verticalAlign: "middle" }}
        >
          <td style={{ textAlign: "center" }}>
            Converting image to text
            <br />
            <br />
            <Spinner animation="border" />
          </td>
          <td></td>
        </tr>
      );
      // Mobile view for pending text
      rows.push(
        <tr className="d-lg-none" key={"mobile-pending-text-" + -1}>
          <td style={{ textAlign: "center" }}>
            Converting image to text
            <br />
            <br />
            <Spinner animation="border" />
          </td>
        </tr>
      );
    }

    return rows;
  }

  render() {
    let qualityDiv = (
      <div className="quality-selector" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px", marginTop: "8px" }}>
        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#555", marginRight: "4px" }}>Quality:</span>
        {[
          { value: "low", label: "Low (~15s)" },
          { value: "medium", label: "Medium (~25s)" },
          { value: "high", label: "High (~50s)" },
        ].map((opt) => (
          <Button
            key={opt.value}
            size="sm"
            className="btn-quality"
            variant={this.state.quality === opt.value ? "primary" : "outline-secondary"}
            onClick={() => this.setState({ quality: opt.value })}
            disabled={this.state.isPendingImage}
            style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "20px" }}
          >
            {opt.label}
          </Button>
        ))}
      </div>
    );

    let modelText = "DALL·E 3"
    if (this.props.imageModel?.startsWith("gpt-image")) {
      modelText = "GPT Image"
    }

    let table_out = <></>;
    if (this.state.outputs.length > 0) {
      table_out = (
        <div style={{ width: "100%" }}>
          <Table striped style={{ width: "100%", tableLayout: "fixed" }}>
            <thead>
              <tr className="d-none d-lg-table-row">
                <th style={{ width: "40%", textAlign: "center" }}>Prompt</th>
                <th style={{ width: "60%", textAlign: "center" }}>
                  {modelText}
                </th>
              </tr>
            </thead>
            <tbody>{this.renderRows()}</tbody>
          </Table>
        </div>
      );
    }

    let reset_button = <></>;
    if (this.state?.outputs?.length > 1) {
      reset_button = (
        <div style={{ textAlign: "right" }}>
          <Button size="sm" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={this.state.clearConfirm ? "danger" : "outline-secondary"} onClick={this.handleClearClick}>
            {this.state.clearConfirm ? "Confirm Clear" : "Clear Images"}
          </Button>
        </div>
      );
    }

    return (
      <Container className="mt-1 mb-0">
        <Row className="mb-0">
          <Col>
            <Card style={{ borderColor: "#c8d7f2" }}>
              <Card.Header
                as="h4"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#dfe8fb",
                  borderColor: "#c8d7f2",
                }}
              >
                Generate Images with {modelText}
                <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                  {this.props.workshopConfig.headerExtra}
                  {
                    this.state?.outputs.length > 1 && (
                      <Button size="sm" className="btn-clear-image" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={this.state.clearConfirm ? "danger" : "outline-secondary"} onClick={this.handleClearClick}>
                        {this.state.clearConfirm ? "Confirm Clear" : "Clear Images"}
                      </Button>
                    )
                  }
                </div>
              </Card.Header>
              <Card.Body style={{ backgroundColor: "#f8fbff" }}>
                <div ref={this.instructionsRef} style={{
                  backgroundColor: "white",
                  border: "2px solid #f05f40",
                  borderRadius: "8px",
                  padding: "10px 15px 15px 15px",
                  margin: "10px 0",
                  boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)"
                }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                    Instructions:
                  </h3>
                  <h4 style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 0 5px 0" }}>
                    {this.props.workshopConfig.instructionTitle}
                  </h4>
                  <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0" }}>
                    {this.props.workshopConfig.instructionSubtitle}
                  </p>
                </div>
                {table_out}
                <hr style={{ marginTop: "40px" }} />
                <Form
                  onSubmit={(e) => {
                    this.generateImage(e);
                  }}
                >
                  <div style={{ position: "relative", backgroundColor: "white", borderRadius: "50px" }}>
                    <div style={{ 
                      position: "absolute", 
                      top: "8px", 
                      left: "50px", 
                      color: "#999", 
                      fontWeight: "bold", 
                      fontSize: "12px",
                      fontStyle: "italic",
                      zIndex: 10,
                      pointerEvents: "none"
                    }}>
                      Prompt for {this.props.imageModel?.startsWith("gpt-image") ? "GPT Image" : "DALL·E 3"}:
                    </div>
                    <div
                      ref={this.overlayRef}
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: "4em",
                        padding: "25px 100px 25px 50px",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        lineHeight: "inherit",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        color: "transparent",
                        pointerEvents: "none",
                        overflow: "hidden",
                        border: "2px solid transparent",
                        borderRadius: "50px",
                        zIndex: 1
                      }}
                    ></div>
                    <Form.Control
                      ref={this.promptRef}
                      autoFocus
                      as="textarea"
                      rows={3}
                      type="text"
                      placeholder={this.props.workshopConfig.placeholder}
                      className="form-control orange-highlight"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          this.generateImage(e);
                        }
                      }}
                      onInput={(e) => {
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = Math.max(64, textarea.scrollHeight) + 'px';
                        if (this.overlayRef.current) {
                          this.overlayRef.current.style.height = textarea.style.height;
                        }
                        this.setState({ highlightStart: -1 });
                        this.syncOverlay();
                      }}
                      onPaste={(e) => {
                        setTimeout(() => {
                          const textarea = e.target;
                          textarea.style.height = 'auto';
                          textarea.style.height = Math.max(64, textarea.scrollHeight) + 'px';
                          if (this.overlayRef.current) {
                            this.overlayRef.current.style.height = textarea.style.height;
                          }
                          this.setState({ highlightStart: -1 });
                          this.syncOverlay();
                        }, 0);
                      }}
                      onScroll={(e) => {
                        if (this.overlayRef.current) {
                          this.overlayRef.current.scrollTop = e.target.scrollTop;
                        }
                      }}
                      style={{ 
                        minHeight: "4em", 
                        position: "relative", 
                        width: "100%",
                        resize: "none",
                        overflow: "hidden",
                        background: "transparent",
                        zIndex: 2
                      }}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-dalle-go"
                      disabled={this.state.isPendingImage}
                    >
                      Go
                    </Button>
                  </div>
                  {qualityDiv}
                </Form>
              </Card.Body>
              <Card.Footer
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  padding: "0.4rem 0.75rem",
                  marginBottom: "0",
                  borderBottom: "none",
                  backgroundColor: "#dfe8fb",
                }}
              >
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  {this.state?.outputs?.length > 1 && (
                    <Button size="sm" className="btn-clear-image" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={this.state.clearConfirm ? "danger" : "outline-secondary"} onClick={this.handleClearClick}>
                      {this.state.clearConfirm ? "Confirm Clear" : "Clear Images"}
                    </Button>
                  )}
                </div>
              </Card.Footer>
            </Card>
            <Button
              className="scroll-arrow"
              onClick={this.scrollTop}
              style={{
                display: this.state.showScroll ? "flex" : "none",
                position: "fixed",
                bottom: "80px",
                right: "20px",
                zIndex: "1000",
              }}
              variant="secondary"
            >
              ↑
            </Button>
            <Button
              className="scroll-arrow"
              onClick={this.scrollBottom}
              style={{
                display: this.state.showScrollBottom ? "flex" : "none",
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: "1000",
              }}
              variant="secondary"
            >
              ↓
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }
}
export { Dalle };
